/**
 * Iter141 — Interviewer Day View.
 *
 * A per-day schedule for interviewers listing every candidate they are
 * meeting on a chosen date, sorted chronologically. No break logic yet —
 * just the schedule.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarShad } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
    Calendar, ChevronLeft, ChevronRight, Clock, User2, Briefcase, MapPin,
    LogOut, Video, Phone, Building2, ListChecks, StickyNote, Plus, Loader2,
} from "lucide-react";

function fmtTimeOnly(iso) {
    if (!iso) return "TBD";
    try {
        return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shiftDate(iso, days) {
    try {
        const [y, m, d] = iso.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
    } catch { return iso; }
}

function longDay(iso) {
    try {
        const [y, m, d] = iso.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
        return dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
    } catch { return iso; }
}

function ModeIcon({ mode }) {
    if (mode === "Phone") return <Phone className="w-3 h-3" />;
    if (mode === "Onsite") return <Building2 className="w-3 h-3" />;
    return <Video className="w-3 h-3" />;
}

function DayRow({ it }) {
    const [open, setOpen] = useState(false);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!body.trim()) return;
        setBusy(true);
        try {
            await api.post(`/interviewer/interview/${it.id}/note`, { body: body.trim() });
            setBody("");
            setOpen(false);
            toast.success("Note added");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div data-testid={`iv-day-row-${it.id}`}>
            <div className="flex items-start gap-4 p-5 hover:bg-secondary transition-colors">
                <div className="w-20 shrink-0 border-r border-border pr-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Start</div>
                    <div className="font-display font-black text-lg">{fmtTimeOnly(it.scheduled_at)}</div>
                    <div className="text-[10px] font-mono text-foreground/60 mt-0.5">{it.duration_minutes || 30} min</div>
                </div>
                <Link to={`/interviewer/interview/${it.id}`} className="min-w-0 flex-1" data-testid={`iv-day-open-${it.id}`}>
                    <div className="font-display font-bold text-lg flex items-center gap-2">
                        <User2 className="w-4 h-4 text-ikb" /> {it.candidate?.name || "Candidate"}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-mono text-foreground/80 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {it.job?.title || "Job"}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><ModeIcon mode={it.mode} /> {it.mode || "Video"}</span>
                        {it.location && (<><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.location}</span></>)}
                    </div>
                </Link>
                <div className="shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest border border-ikb bg-blue-50 text-ikb px-2 py-1">
                        {it.round_name || "Round"}
                    </span>
                    {it.status && it.status !== "Scheduled" && (
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-border bg-secondary text-foreground/70 px-2 py-0.5">{it.status}</span>
                    )}
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb px-2 py-1 inline-flex items-center gap-1 mt-1"
                        data-testid={`iv-day-note-toggle-${it.id}`}
                    >
                        <StickyNote className="w-3 h-3" /> {open ? "Close note" : "Quick note"}
                    </button>
                </div>
            </div>
            {open && (
                <div className="px-5 pb-4 bg-secondary/40" data-testid={`iv-day-note-${it.id}`}>
                    <Textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Add a quick note for this candidate — it will append to the recruiter's notes."
                        rows={3}
                        className="rounded-none text-sm"
                        data-testid={`iv-day-note-body-${it.id}`}
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                        <Button
                            onClick={submit}
                            disabled={busy || !body.trim()}
                            className="rounded-none h-8 bg-ikb hover:bg-[#001f7a] text-white text-xs"
                            data-testid={`iv-day-note-submit-${it.id}`}
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Plus className="w-3 h-3 mr-1.5" />}
                            {busy ? "Saving…" : "Save note"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function InterviewerDay() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState({ interviews: [], count: 0 });
    const [loading, setLoading] = useState(true);

    const load = async (d) => {
        setLoading(true);
        try {
            const tzOffset = new Date().getTimezoneOffset();
            const { data } = await api.get("/interviewer/day", { params: { date: d, tz_offset: tzOffset } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); setData({ interviews: [], count: 0 }); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

    const totalMinutes = useMemo(() => (data.interviews || []).reduce((a, b) => a + (b.duration_minutes || 30), 0), [data]);

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-white sticky top-0 z-30">
                <div className="flex items-center h-16 px-6 gap-6">
                    <div className="flex items-center gap-2">
                        <BrandLogo size={28} />
                        <div className="flex flex-col leading-tight">
                            <span className="font-display font-black tracking-tight text-base">{branding.short_name}</span>
                            <span className="text-[10px] uppercase tracking-widest font-mono text-ikb">Interviewer · Day planner</span>
                        </div>
                    </div>
                    <nav className="ml-6 hidden md:flex items-center gap-1">
                        <Link to="/interviewer" className="text-xs font-mono uppercase tracking-widest border border-border px-3 py-1.5 hover:border-ikb hover:text-ikb" data-testid="iv-day-nav-upcoming">
                            <ListChecks className="w-3 h-3 inline mr-1" /> Upcoming
                        </Link>
                        <span className="text-xs font-mono uppercase tracking-widest bg-ikb text-white px-3 py-1.5" data-testid="iv-day-nav-day">
                            <Calendar className="w-3 h-3 inline mr-1" /> Day view
                        </span>
                    </nav>
                    <div className="ml-auto flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-2 text-xs">
                            <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                {(user?.name || "").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="leading-tight">
                                <div className="font-medium">{user?.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-foreground/70">Interviewer</div>
                            </div>
                        </div>
                        <Button variant="outline" className="rounded-none h-9" onClick={async () => { await logout(); nav("/login"); }} data-testid="iv-day-logout">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-3 sm:px-4 py-5 space-y-5" data-testid="iv-day-page">
                {/* Date switcher */}
                <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => setDate((d) => shiftDate(d, -1))} className="h-9 w-9 inline-flex items-center justify-center border border-border bg-white hover:border-ikb hover:text-ikb" data-testid="iv-day-prev">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="h-9 px-3 border border-border bg-white text-sm font-mono inline-flex items-center gap-2 hover:border-ikb hover:text-ikb"
                                data-testid="iv-day-date-input"
                            >
                                <Calendar className="w-4 h-4" />
                                {date}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="p-0 w-auto" data-testid="iv-day-date-popover">
                            <CalendarShad
                                mode="single"
                                selected={(() => { try { const [y, m, d] = date.split("-").map(Number); return new Date(y, m - 1, d); } catch { return undefined; } })()}
                                onSelect={(d) => {
                                    if (!d) return;
                                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                                    setDate(iso);
                                }}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <button type="button" onClick={() => setDate((d) => shiftDate(d, 1))} className="h-9 w-9 inline-flex items-center justify-center border border-border bg-white hover:border-ikb hover:text-ikb" data-testid="iv-day-next">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setDate(todayISO())} className="h-9 px-3 border border-border bg-white text-xs font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb" data-testid="iv-day-today">
                        Today
                    </button>
                    <span className="ml-2 font-display font-bold text-lg" data-testid="iv-day-title">{longDay(date)}</span>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="border border-border bg-white p-4" data-testid="iv-day-summary-count">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Interviews</div>
                        <div className="font-display font-black text-3xl mt-1">{data.count}</div>
                    </div>
                    <div className="border border-border bg-white p-4" data-testid="iv-day-summary-minutes">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Total time</div>
                        <div className="font-display font-black text-3xl mt-1">{totalMinutes}<span className="text-base font-normal text-foreground/60"> min</span></div>
                    </div>
                    <div className="border border-border bg-white p-4" data-testid="iv-day-summary-done">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Completed</div>
                        <div className="font-display font-black text-3xl mt-1">{(data.interviews || []).filter((i) => i.status === "Completed").length}</div>
                    </div>
                </div>

                {/* Schedule */}
                {loading ? (
                    <div className="text-sm font-mono text-foreground/80">Loading day…</div>
                ) : data.interviews.length === 0 ? (
                    <div className="border border-dashed border-border bg-white p-16 text-center" data-testid="iv-day-empty">
                        <h3 className="font-display font-bold text-xl">No interviews on this day</h3>
                        <p className="text-sm text-foreground/80 mt-2">Pick another date or head back to Upcoming to see all scheduled rounds.</p>
                    </div>
                ) : (
                    <div className="border border-border bg-white divide-y divide-border" data-testid="iv-day-list">
                        {data.interviews.map((it) => (
                            <DayRow key={it.id} it={it} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
